import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Avatar,
  IconButton,
  Alert,
  styled,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ResponsiveContainer from '../components/responsive/ResponsiveContainer';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import VerifiedIcon from '@mui/icons-material/Verified';
import ShareIcon from '@mui/icons-material/Share';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../contexts/AuthContext';
import { formatLocationDisplay } from '../utils/formatting';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(2),
}));

const EventDetail = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(1.5),
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  margin: theme.spacing(3, 0),
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
}));

const PriceTag = styled(Box)(({ theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(1, 2),
  color: theme.palette.primary.main,
  fontWeight: 'bold',
  fontSize: '1.5rem',
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.primary.main}`,
  marginBottom: theme.spacing(2),
}));

const ShareContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center'
  }
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  }
}));

// Interface for route parameters
interface RouteParams {
  [key: string]: string | undefined;
  organizationId?: string;
  eventId?: string;
}

// Interface for ticket types
interface TicketType {
  id: string;
  name: string;
  price: number;
  description?: string;
  available: boolean;
}

// Interface for the event
interface Event {
  id: string;
  title: string;
  description: string;
  startDate: Date | { seconds: number; nanoseconds: number };
  endDate?: Date | { seconds: number; nanoseconds: number };
  location: string;
  imageUrl: string;
  price: number;
  ticketTypes: TicketType[];
  capacity: number;
  registeredCount: number;
  category: string;
}

// Interface for the organization
interface Organization {
  id: string;
  name: string;
  logo: string;
  description: string;
  website?: string;
  location?: string;
  memberCount?: number;
  foundedYear?: number;
  verified: boolean;
}

const EventDetailsPage: React.FC = () => {
  const params = useParams<RouteParams>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const organizationId = params.organizationId || '';
  const eventId = params.eventId || '';

  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [event, setEvent] = useState<Event | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);

  // Format date
  const formatDate = (date: Date | { seconds: number; nanoseconds: number }) => {
    const dateObj = date instanceof Date ? date : new Date((date as any).seconds * 1000);
    return dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (date: Date | { seconds: number; nanoseconds: number }) => {
    const dateObj = date instanceof Date ? date : new Date((date as any).seconds * 1000);
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle copy link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 3000);
  };

  // Fetch event and organization details
  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);

        if (!organizationId || !eventId) {
          setError('Invalid event link');
          setLoading(false);
          return;
        }

        // In a real app, these would be Firestore queries
        // For demo purposes, we'll use hard-coded data

        // Mock event data
        const mockEvent: Event = {
          id: eventId,
          title: 'Summer Hiking Adventure',
          description: 'Join us for a day of hiking through the beautiful trails of Rocky Mountain National Park. This event is suitable for all experience levels. We will meet at the visitor center and travel together to the trailhead. Guides will be available to assist and ensure a safe and enjoyable experience. Lunch and refreshments will be provided. Please bring appropriate footwear, sun protection, and a reusable water bottle. In case of severe weather, a backup date will be announced.',
          startDate: new Date(2025, 5, 15, 9, 0), // June 15, 2025, 9:00 AM
          endDate: new Date(2025, 5, 15, 16, 0), // June 15, 2025, 4:00 PM
          location: 'Rocky Mountain National Park',
          imageUrl: 'https://picsum.photos/seed/event1/600/400',
          price: 25,
          ticketTypes: [
            {
              id: 'ticket1',
              name: 'Standard Pass',
              price: 25,
              description: 'Access to the guided hiking tour and lunch',
              available: true
            },
            {
              id: 'ticket2',
              name: 'Premium Pass',
              price: 40,
              description: 'Includes equipment rental, guided tour, and lunch',
              available: true
            }
          ],
          capacity: 50,
          registeredCount: 32,
          category: 'outdoor'
        };

        // Mock organization data
        const mockOrganization: Organization = {
          id: organizationId,
          name: 'Adventure Seekers',
          logo: 'https://picsum.photos/seed/org1/200/200',
          description: 'Adventure Seekers connects outdoor enthusiasts with thrilling experiences around the world.',
          website: 'https://www.adventureseekers.com',
          location: 'Denver, Colorado',
          memberCount: 5000,
          foundedYear: 2010,
          verified: true
        };

        setEvent(mockEvent);
        setOrganization(mockOrganization);

      } catch (err) {
        console.error('Error fetching event details:', err);
        setError('Failed to load event details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEventDetails();
  }, [organizationId, eventId]);

  // Handle buy tickets
  const handleBuyTickets = () => {
    if (event && organization) {
      navigate(`/ticket-purchase/${organization.id}/${event.id}`);
    }
  };

  // Handle social sharing
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = event?.title || 'Event';
    
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(title + ' ' + url)}`;
        break;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
  };

  // Get responsive breakpoints
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  return (
    <ResponsiveContainer maxWidth="lg" sx={{ 
      my: { xs: 1, md: 4 },
      px: { xs: 1, sm: 2 },
      minHeight: '100vh',
      bgcolor: isMobile ? 'grey.50' : 'background.default',
      pb: isMobile ? 8 : 0
    }} bottomNavPadding>
      {loading ? (
        <LoadingContainer>
          <CircularProgress />
          <Typography sx={{ mt: 2, fontSize: isMobile ? '0.9rem' : '1rem' }}>Loading...</Typography>
        </LoadingContainer>
      ) : error ? (
        <Alert severity="error" sx={{ 
          mb: 3,
          fontSize: isMobile ? '0.8rem' : '0.875rem'
        }}>{error}</Alert>
      ) : (
        <>
          {event && organization && (
            <>
              {/* Event Header */}
              <Card sx={{ 
                mb: isMobile ? 2 : 4,
                borderRadius: isMobile ? 2 : 3,
                overflow: 'hidden',
                boxShadow: isMobile ? 1 : 3
              }}>
                <CardMedia
                  component="img"
                  height={isMobile ? "220" : isTablet ? "300" : "400"}
                  image={event.imageUrl}
                  alt={event.title}
                  sx={{
                    objectFit: 'cover',
                    '&:hover': {
                      transform: isMobile ? 'none' : 'scale(1.02)',
                      transition: 'transform 0.3s ease-in-out'
                    }
                  }}
                />
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    mb: isMobile ? 1.5 : 1,
                    flexWrap: 'wrap',
                    gap: 0.5
                  }}>
                    <Avatar
                      src={organization.logo}
                      alt={organization.name}
                      sx={{ 
                        width: isMobile ? 32 : 40, 
                        height: isMobile ? 32 : 40, 
                        mr: 1 
                      }}
                    />
                    <Typography 
                      variant="subtitle1" 
                      color="text.secondary"
                      sx={{ 
                        fontSize: isMobile ? '0.8rem' : '1rem',
                        fontWeight: 500
                      }}
                    >
                      {organization.name}
                      {organization.verified && (
                        <VerifiedIcon
                          color="primary"
                          fontSize="small"
                          sx={{ ml: 0.5, fontSize: isMobile ? '0.8rem' : '1rem' }}
                          titleAccess="Verified Organization"
                        />
                      )}
                    </Typography>
                  </Box>

                  <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    component="h1" 
                    gutterBottom
                    sx={{ 
                      fontSize: isMobile ? '1.3rem' : '2.125rem',
                      lineHeight: isMobile ? 1.2 : 1.3,
                      fontWeight: 700,
                      wordBreak: 'break-word'
                    }}
                  >
                    {event.title}
                  </Typography>

                  <Box sx={{ my: isMobile ? 1.5 : 2 }}>
                    <Chip 
                      label={categories[event.category] || event.category} 
                      color="primary" 
                      variant="outlined"
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        fontSize: isMobile ? '0.7rem' : '0.875rem',
                        height: isMobile ? '24px' : '32px'
                      }}
                    />
                  </Box>

                  <Grid container spacing={isMobile ? 2 : 3}>
                    <Grid item xs={12} md={8} sx={{ mb: isMobile ? 2 : 0 }}>
                      <EventDetail sx={{ mb: isMobile ? 1 : 1.5 }}>
                        <EventIcon sx={{ 
                          mr: 1, 
                          color: 'text.secondary',
                          fontSize: isMobile ? '1.1rem' : '1.25rem'
                        }} />
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            fontWeight: 500
                          }}
                        >
                          {formatDate(event.startDate)}
                        </Typography>
                      </EventDetail>

                      <EventDetail sx={{ mb: isMobile ? 1 : 1.5 }}>
                        <AccessTimeIcon sx={{ 
                          mr: 1, 
                          color: 'text.secondary',
                          fontSize: isMobile ? '1.1rem' : '1.25rem'
                        }} />
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            fontWeight: 500
                          }}
                        >
                          {formatTime(event.startDate)}
                          {event.endDate && ` - ${formatTime(event.endDate)}`}
                        </Typography>
                      </EventDetail>

                      <EventDetail sx={{ mb: isMobile ? 1 : 1.5 }}>
                        <LocationOnIcon sx={{ 
                          mr: 1, 
                          color: 'text.secondary',
                          fontSize: isMobile ? '1.1rem' : '1.25rem'
                        }} />
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            fontWeight: 500,
                            wordBreak: 'break-word'
                          }}
                        >
                          {formatLocationDisplay(event.location)}
                        </Typography>
                      </EventDetail>

                      <EventDetail sx={{ mb: isMobile ? 1 : 1.5 }}>
                        <PeopleIcon sx={{ 
                          mr: 1, 
                          color: 'text.secondary',
                          fontSize: isMobile ? '1.1rem' : '1.25rem'
                        }} />
                        <Typography 
                          variant="body1"
                          sx={{ 
                            fontSize: isMobile ? '0.9rem' : '1rem',
                            fontWeight: 500
                          }}
                        >
                          {event.registeredCount} / {event.capacity} registered
                        </Typography>
                      </EventDetail>
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: { xs: 'stretch', md: 'flex-end' },
                      justifyContent: 'center',
                      gap: isMobile ? 1.5 : 2
                    }}>
                      <PriceTag sx={{
                        fontSize: isMobile ? '1.2rem' : '1.5rem',
                        padding: isMobile ? '8px 16px' : '12px 24px',
                        textAlign: isMobile ? 'center' : 'left',
                        width: isMobile ? '100%' : 'auto'
                      }}>
                        From ${event.price.toFixed(2)}
                      </PriceTag>
                      <Button 
                        variant="contained"
                        color="primary"
                        onClick={handleBuyTickets}
                        fullWidth={isMobile}
                        size={isMobile ? 'large' : 'medium'}
                        sx={{ 
                          minWidth: { xs: '100%', sm: 'auto' },
                          fontSize: isMobile ? '0.9rem' : '0.875rem',
                          py: isMobile ? 1.5 : 1,
                          px: isMobile ? 3 : 2,
                          fontWeight: 600,
                          borderRadius: isMobile ? 2 : 1
                        }}
                      >
                        {isMobile ? 'Get Tickets' : 'Select Tickets'}
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Event Details */}
              <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={8}>
                  <StyledPaper sx={{ 
                    p: isMobile ? 2 : 3,
                    borderRadius: isMobile ? 2 : 3,
                    boxShadow: isMobile ? 1 : 2
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      gutterBottom
                      sx={{ 
                        fontSize: isMobile ? '1.1rem' : '1.5rem',
                        fontWeight: 600,
                        mb: isMobile ? 1.5 : 2
                      }}
                    >
                      Event Description
                    </Typography>
                    <Typography 
                      variant="body1" 
                      paragraph
                      sx={{ 
                        fontSize: isMobile ? '0.9rem' : '1rem',
                        lineHeight: isMobile ? 1.5 : 1.6,
                        textAlign: 'justify'
                      }}
                    >
                      {event.description}
                    </Typography>

                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      gutterBottom 
                      sx={{ 
                        mt: isMobile ? 3 : 4,
                        fontSize: isMobile ? '1rem' : '1.25rem',
                        fontWeight: 600,
                        mb: isMobile ? 1.5 : 2
                      }}
                    >
                      Available Tickets
                    </Typography>
                    
                    {event.ticketTypes.map((ticket) => (
                      <Card key={ticket.id} sx={{ 
                        mb: isMobile ? 1.5 : 2,
                        borderRadius: isMobile ? 2 : 1,
                        boxShadow: isMobile ? 1 : 2
                      }}>
                        <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between', 
                            alignItems: { xs: 'stretch', sm: 'flex-start' },
                            gap: { xs: 2, sm: 0 }
                          }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography 
                                variant={isMobile ? "subtitle1" : "h6"}
                                sx={{ 
                                  fontSize: isMobile ? '1rem' : '1.25rem',
                                  fontWeight: 600,
                                  mb: 0.5
                                }}
                              >
                                {ticket.name}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="text.secondary" 
                                sx={{ 
                                  mb: 1,
                                  fontSize: isMobile ? '0.8rem' : '0.875rem',
                                  lineHeight: 1.4
                                }}
                              >
                                {ticket.description}
                              </Typography>
                              <Chip 
                                label={ticket.available ? 'Available' : 'Sold Out'} 
                                color={ticket.available ? 'success' : 'error'} 
                                size="small"
                                sx={{
                                  fontSize: isMobile ? '0.7rem' : '0.75rem',
                                  height: isMobile ? '20px' : '24px'
                                }}
                              />
                            </Box>
                            <Typography 
                              variant={isMobile ? "h6" : "h6"} 
                              color="primary"
                              sx={{ 
                                fontSize: isMobile ? '1.1rem' : '1.25rem',
                                fontWeight: 700,
                                textAlign: isMobile ? 'center' : 'right',
                                mt: isMobile ? 1 : 0
                              }}
                            >
                              ${ticket.price.toFixed(2)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    ))}

                    <ShareContainer sx={{
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'stretch' : 'center',
                      gap: isMobile ? 1.5 : 1,
                      mt: isMobile ? 3 : 2
                    }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          mr: isMobile ? 0 : 2,
                          fontSize: isMobile ? '0.9rem' : '1rem',
                          fontWeight: 600,
                          textAlign: isMobile ? 'center' : 'left'
                        }}
                      >
                        Share this event:
                      </Typography>
                      <Box sx={{
                        display: 'flex',
                        justifyContent: isMobile ? 'center' : 'flex-start',
                        gap: isMobile ? 1 : 0.5,
                        flexWrap: 'wrap'
                      }}>
                        <SocialIconButton 
                          color="primary" 
                          onClick={() => handleShare('facebook')}
                          sx={{
                            p: isMobile ? 1.5 : 1,
                            '&:hover': { bgcolor: 'primary.light' }
                          }}
                        >
                          <FacebookIcon sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                        </SocialIconButton>
                        <SocialIconButton 
                          color="primary" 
                          onClick={() => handleShare('twitter')}
                          sx={{
                            p: isMobile ? 1.5 : 1,
                            '&:hover': { bgcolor: 'primary.light' }
                          }}
                        >
                          <TwitterIcon sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                        </SocialIconButton>
                        <SocialIconButton 
                          color="primary" 
                          onClick={() => handleShare('linkedin')}
                          sx={{
                            p: isMobile ? 1.5 : 1,
                            '&:hover': { bgcolor: 'primary.light' }
                          }}
                        >
                          <LinkedInIcon sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                        </SocialIconButton>
                        <SocialIconButton 
                          color="primary" 
                          onClick={() => handleShare('whatsapp')}
                          sx={{
                            p: isMobile ? 1.5 : 1,
                            '&:hover': { bgcolor: 'primary.light' }
                          }}
                        >
                          <WhatsAppIcon sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                        </SocialIconButton>
                        <SocialIconButton 
                          color="primary" 
                          onClick={handleCopyLink}
                          sx={{
                            p: isMobile ? 1.5 : 1,
                            '&:hover': { bgcolor: 'primary.light' }
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }} />
                        </SocialIconButton>
                      </Box>
                      {copySuccess && (
                        <Typography 
                          variant="caption" 
                          color="success.main"
                          sx={{ 
                            textAlign: 'center',
                            fontSize: isMobile ? '0.8rem' : '0.75rem',
                            fontWeight: 500
                          }}
                        >
                          Link copied!
                        </Typography>
                      )}
                    </ShareContainer>
                  </StyledPaper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <StyledPaper sx={{ 
                    p: isMobile ? 2 : 3,
                    borderRadius: isMobile ? 2 : 3,
                    boxShadow: isMobile ? 1 : 2
                  }}>
                    <Typography 
                      variant={isMobile ? "h6" : "h5"} 
                      gutterBottom
                      sx={{ 
                        fontSize: isMobile ? '1.1rem' : '1.5rem',
                        fontWeight: 600,
                        mb: isMobile ? 1.5 : 2
                      }}
                    >
                      About the Organizer
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: isMobile ? 1.5 : 2,
                      flexDirection: isMobile ? 'column' : 'row',
                      textAlign: isMobile ? 'center' : 'left'
                    }}>
                      <Avatar
                        src={organization.logo}
                        alt={organization.name}
                        sx={{ 
                          width: isMobile ? 50 : 60, 
                          height: isMobile ? 50 : 60, 
                          mr: isMobile ? 0 : 2,
                          mb: isMobile ? 1 : 0
                        }}
                      />
                      <Box>
                        <Typography 
                          variant={isMobile ? "subtitle1" : "h6"}
                          sx={{ 
                            fontSize: isMobile ? '1rem' : '1.25rem',
                            fontWeight: 600
                          }}
                        >
                          {organization.name}
                          {organization.verified && (
                            <VerifiedIcon
                              color="primary"
                              fontSize="small"
                              sx={{ 
                                ml: 0.5,
                                fontSize: isMobile ? '0.8rem' : '1rem'
                              }}
                              titleAccess="Verified Organization"
                            />
                          )}
                        </Typography>
                        {organization.location && (
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              fontSize: isMobile ? '0.8rem' : '0.875rem'
                            }}
                          >
                            {organization.location}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      paragraph
                      sx={{ 
                        fontSize: isMobile ? '0.9rem' : '0.875rem',
                        lineHeight: isMobile ? 1.5 : 1.6,
                        textAlign: isMobile ? 'center' : 'left'
                      }}
                    >
                      {organization.description}
                    </Typography>
                    
                    <StyledDivider sx={{ my: isMobile ? 1.5 : 2 }} />
                    
                    {organization.website && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: 1,
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          textAlign: isMobile ? 'center' : 'left'
                        }}
                      >
                        <strong>Website:</strong>{' '}
                        <a 
                          href={organization.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            color: 'inherit',
                            textDecoration: 'underline',
                            wordBreak: 'break-all'
                          }}
                        >
                          {organization.website.replace(/^https?:\/\//, '')}
                        </a>
                      </Typography>
                    )}
                    
                    {organization.foundedYear && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mb: 1,
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          textAlign: isMobile ? 'center' : 'left'
                        }}
                      >
                        <strong>Founded:</strong> {organization.foundedYear}
                      </Typography>
                    )}
                    
                    {organization.memberCount && (
                      <Typography 
                        variant="body2"
                        sx={{ 
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          textAlign: isMobile ? 'center' : 'left'
                        }}
                      >
                        <strong>Members:</strong> {organization.memberCount.toLocaleString()}
                      </Typography>
                    )}
                    
                    <Box sx={{ mt: isMobile ? 2 : 3 }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate(`/explore-events/?organizationId=${organization.id}`)}
                        fullWidth={isMobile}
                        size={isMobile ? 'medium' : 'medium'}
                        sx={{
                          fontSize: isMobile ? '0.8rem' : '0.875rem',
                          py: isMobile ? 1 : 1,
                          borderRadius: isMobile ? 2 : 1
                        }}
                      >
                        {isMobile ? 'More Events' : 'View More Events'}
                      </Button>
                    </Box>
                  </StyledPaper>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </ResponsiveContainer>
  );
};

// Event categories
const categories: Record<string, string> = {
  'all': 'All Categories',
  'outdoor': 'Outdoor Activities',
  'workshop': 'Workshops & Classes',
  'travel': 'Travel & Tours',
  'food': 'Food & Dining',
  'culture': 'Arts & Culture',
  'social': 'Social Events',
  'business': 'Business & Networking',
  'sports': 'Sports & Fitness'
};

export default EventDetailsPage;
